/* eslint-disable spaced-comment */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { FormGroup, ModalFooter, Label } from "reactstrap";
import useFetcher from "../../../hooks/useFetcher";
import { fetchNovedadPiAsignable } from "../../../services/novedadesServices";
import { StyledSelect } from "../../../components/StyledSelect";
import { handleErrors } from "../../../services/handleErrors";
import SpinnerButton from "../../../components/Button";
import { updateAreaCentralizado, postSubclientArea } from "../../../services/areaServices";
import ConfirmCentralizado from "./ConfirmCentralizado";
import ConfirmModal from "./ConfirmModal";
import compareDataChanges from "../../../utils/compareDataChanges";
import { useAuth } from "../../../context/AuthContext";
import modulos from "../../../mocks/modulos";
import { areaUtil } from "../../../utils/areaUtil";

function Cobranzas({ centralizado, ClientId, subclientAreaData, handleChangesToSave }) {
  const { currentUser } = useAuth();
  const isAdmin = currentUser && currentUser.haveAdminAccess(modulos.CLIENTES);
  const isAudit = currentUser && currentUser.haveAuditAccess(modulos.CLIENTES);
  const isOwner = useMemo(() => areaUtil(subclientAreaData).isOwner(currentUser.user), [subclientAreaData, currentUser.user]);
  const readonly = (!isAdmin && !isOwner) || isAudit;

  const [confirmarModal, setConfirmarModal] = useState(false);
  const [centralizadoModal, setCentralizadoModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [changes, setChanges] = useState({ responsableId: false, liderId: false, observaciones: false });

  const formik = useFormik({
    initialValues: {
      responsableId: (subclientAreaData && subclientAreaData.responsableId) || null,
      liderId: (subclientAreaData && subclientAreaData.liderId) || null,
      observaciones: (subclientAreaData && subclientAreaData.observaciones) || "",
    },
    onSubmit: async (area) => {
      setIsLoading(true);
      const filteredInfo = compareDataChanges(area, subclientAreaData);
      try {
        if (subclientAreaData) {
          await updateAreaCentralizado(subclientAreaData.id, ClientId, filteredInfo);
        } else {
          await postSubclientArea(filteredInfo);
        }
        setConfirmarModal(false);
        setCentralizadoModal(false);
        handleChangesToSave(false);
        setChanges({ responsableId: false, liderId: false, observaciones: false });
        toast.success("Área actualizada");
        setIsLoading(false);
      } catch (error) {
        handleErrors(error, toast.error);
        setIsLoading(false);
      }
    },
    validationSchema: Yup.object().shape({
      responsableId: Yup.number().nullable(),
      liderId: Yup.number().nullable(),
      observaciones: Yup.string().nullable().notRequired(),
    }),
  });

  const [pisAsignable, isLoadingAsignable] = useFetcher(fetchNovedadPiAsignable);

  const piOptions = pisAsignable
    ? pisAsignable.map((assigned) => {
        return { label: assigned.nombreApellido, value: assigned.itemId };
      })
    : [];

  const responsableValue = piOptions.find((option) => {
    return option.value === formik.values.responsableId;
  });

  const liderValue = piOptions.find((option) => {
    return option.value === formik.values.liderId;
  });

  const handleBlur = (value) => {
    if (subclientAreaData) {
      if (formik.values[value] == null) {
        handleChangesToSave(true);
        setChanges((prevState) => ({ ...prevState, [value]: true }));
        return;
      }
      if (formik.values[value] !== subclientAreaData[value]) {
        handleChangesToSave(true);
        setChanges((prevState) => ({ ...prevState, [value]: true }));
        return;
      }
      if (formik.values[value] === subclientAreaData[value]) {
        handleChangesToSave(false);
        setChanges((prevState) => ({ ...prevState, [value]: false }));
        return;
      }
    }
    if (formik.values[value] != null || formik.values[value] !== "") {
      handleChangesToSave(true);
      setChanges((prevState) => ({ ...prevState, [value]: true }));
    } else {
      handleChangesToSave(false);
      setChanges((prevState) => ({ ...prevState, [value]: false }));
    }
  };

  return (
    <>
      {confirmarModal && <ConfirmModal isLoading={isLoading} toggle={() => setConfirmarModal(!confirmarModal)} handleSubmit={formik.handleSubmit} />}
      {centralizadoModal && (
        <ConfirmCentralizado
          ClientId={ClientId}
          area="cobranzas"
          areaColor="#e35205"
          isLoading={isLoading}
          toggle={() => setCentralizadoModal(!centralizadoModal)}
          handleSubmit={formik.handleSubmit}
        />
      )}
      <FormGroup className="d-flex justify-content-between">
        <Label htmlFor="responsable" className="col-form-label pt-0 w-100">
          Responsable
          <StyledSelect
            id="responsableId"
            name="responsableId"
            className="w-100"
            formik={formik}
            required
            value={responsableValue || null}
            placeholder="Seleccione responsable"
            isClearable
            disabled={formik.isSubmitting || isLoadingAsignable}
            isLoading={isLoadingAsignable}
            options={piOptions}
            onBlur={() => handleBlur("responsableId")}
            isDisabled={readonly}
          />
          {changes.responsableId ? <div className="input-feedback">Cambios por guardar</div> : null}
        </Label>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between">
        <Label htmlFor="lider" className="col-form-label pt-0 w-100">
          Líder
          <StyledSelect
            id="liderId"
            name="liderId"
            className="w-100"
            formik={formik}
            required
            value={liderValue || null}
            placeholder="Seleccione lider"
            isClearable
            isLoading={isLoadingAsignable}
            disabled={formik.isSubmitting || isLoadingAsignable}
            options={piOptions}
            onBlur={() => handleBlur("liderId")}
            isDisabled={readonly}
          />
          {changes.liderId ? <div className="input-feedback">Cambios por guardar</div> : null}
        </Label>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="observaciones" className="col-form-label pt-0 w-100">
            Observaciones
          </Label>
          <textarea
            name="observaciones"
            cols="63"
            rows="3"
            value={formik.values.observaciones}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("observaciones")}
            disabled={readonly}
          />
          {changes.observaciones ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <ModalFooter>
        <FormGroup className="mb-0 px-3">
          {!readonly && (
            <SpinnerButton
              className="btn btn-primary btn-block"
              isLoading={false}
              disabled={false}
              onClick={() => (centralizado ? setCentralizadoModal(!centralizadoModal) : setConfirmarModal(!confirmarModal))}
            >
              Guardar Cambios
            </SpinnerButton>
          )}
        </FormGroup>
      </ModalFooter>
    </>
  );
}

export default Cobranzas;
